import React, { useState, useMemo } from "react";
import { Box, Select, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { useTasksTab } from "../../hooks/Tasks/useTasksTab";
import { PRIMARY_COLOR } from "./constants";
import AddPoiDialog from "./AddPoiDialog";
import { tokens } from "../../theme/tokens";

interface Props {
  leftSlot?: React.ReactNode;
}

const TasksTopBar: React.FC<Props> = ({ leftSlot }) => {
  const {
    pois,
    activePoiId,
    setActivePoiId,
    tasks,
    setSelectedTaskId,
    openNewTaskSheet,
    createPoi,
  } = useTasksTab();

  const [addPoiOpen, setAddPoiOpen] = useState(false);

  const taskCountByPoi = useMemo(
    () =>
      tasks.reduce<Record<string, number>>((acc, t) => {
        acc[t.poi.id] = (acc[t.poi.id] ?? 0) + 1;
        return acc;
      }, {}),
    [tasks],
  );

  return (
    <Box
      sx={{
        backgroundColor: tokens.color.surface.default,
        borderBottom: `1px solid ${tokens.color.border.default}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: "16px",
        py: "10px",
        flexShrink: 0,
        gap: "12px",
      }}
    >
      {leftSlot}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
        <span
          style={{
            fontSize: "11px",
            fontWeight: 600,
            color: tokens.color.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          POI
        </span>
        <Select
          size="small"
          value={activePoiId ?? ""}
          onChange={(e) => {
            const val = e.target.value as string;
            setActivePoiId(val === "" ? null : val);
            setSelectedTaskId(null);
          }}
          displayEmpty
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            height: "28px",
            minWidth: "160px",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.color.border.default },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tokens.color.border.strong },
            "& .MuiSelect-select": { py: "4px" },
          }}
        >
          <MenuItem
            value=""
            sx={{ fontSize: "12px", display: "flex", justifyContent: "space-between", gap: "12px" }}
          >
            <span>All POIs</span>
            <span style={{ color: tokens.color.text.secondary, fontSize: "11px", marginLeft: "6px" }}>
              {tasks.length}
            </span>
          </MenuItem>
          {pois.map((poi) => (
            <MenuItem
              key={poi.id}
              value={poi.id}
              sx={{ fontSize: "12px", display: "flex", justifyContent: "space-between", gap: "12px" }}
            >
              <span>{poi.is_primary ? `${poi.name} ★` : poi.name}</span>
              <span style={{ color: tokens.color.text.secondary, fontSize: "11px", marginLeft: "6px" }}>
                {taskCountByPoi[poi.id] ?? 0}
              </span>
            </MenuItem>
          ))}
        </Select>

        <button
          type="button"
          onClick={() => setAddPoiOpen(true)}
          title="Add new subject / POI"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "11px",
            fontWeight: 500,
            color: tokens.color.text.secondary,
            backgroundColor: "transparent",
            border: `1px dashed ${tokens.color.border.strong}`,
            borderRadius: "6px",
            padding: "3px 10px",
            cursor: "pointer",
            height: "26px",
          }}
        >
          <PersonAddOutlinedIcon style={{ fontSize: "13px" }} />
          Add POI
        </button>
      </Box>

      <button
        type="button"
        onClick={openNewTaskSheet}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          fontSize: "12px",
          fontWeight: 600,
          color: tokens.color.surface.default,
          backgroundColor: PRIMARY_COLOR,
          border: "none",
          borderRadius: "7px",
          padding: "8px 14px",
          cursor: "pointer",
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        <AddIcon style={{ fontSize: "15px" }} />
        New Task
      </button>

      <AddPoiDialog
        open={addPoiOpen}
        onClose={() => setAddPoiOpen(false)}
        onCreate={(firstName, lastName, subjectType) => {
          createPoi(firstName, lastName, subjectType);
          setAddPoiOpen(false);
        }}
      />
    </Box>
  );
};

export default TasksTopBar;
